import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs/';

import { CommunicationService } from './communication.service';
import { BasicError, Error403 } from '../models/error/error';

import { User } from '../models/user';
import { Thread } from '../models/thread';
import { Message } from '../models/messages';

@Injectable()
export class MessageService {
  private allMessagesURL = '/api/v1/messages';
  private createNewThreadURL = '/api/v1/messages/threads';
  private threadDetailURL = '/api/v1/messages/threads/';
  private sendMessageURL = '/api/v1/messages/threads/##id##/messages';
  private addParticipantsURL = '/api/v1/messages/threads/##id##/relationships/participants';
  private deleteThreadURL = '/api/v1/messages/threads/';
  private updateReplay: Subject<any> = new Subject();
  public serviceStorage = {};

  constructor(private communication: CommunicationService) { }

  /**
   * This function returns all messages from the current user.
   * Without filtering on study
   */
  public getAllMessages(): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.allMessagesURL + '?limit=0&include=participants,messages').subscribe(json => {
      if (json instanceof BasicError) {
        console.log('------------MessageService--------: Failed to get Threads of user.');
        retReplay.next(json);
        return;
      }

      // No Error occured, now get the threads
      const threads: Array<Thread> = [];
      const users = {};
      const messages = {};

      // Retrieve users if some where returned
      if (json.included !== undefined) {
        for (let i = 0; i < json.included.length; i++) {
          const include = json.included[i];

          if (include.type !== undefined && include.type === 'users') {
            const user = new User(include.id);

            if (include.attributes.name !== undefined) { user.setName(include.attributes.name); }
            if (include.attributes.email !== undefined) { user.setEmail(include.attributes.email); }
            if (include.attributes.firstname !== undefined) { user.setFirstName(include.attributes.firstname); }
            if (include.attributes.lastname !== undefined) { user.setLastName(include.attributes.lastname); }
            if (include.attributes.picture !== undefined) { user.setPicture(include.attributes.picture); }

            users[include.id] = user;
          } else if (include.type !== undefined && include.type === 'messages/messages') {
            const message = new Message(include.id);
            if (include.attributes.body !== undefined) {
              message.setMessage(include.attributes.body);
            }
            if (include.attributes.deliver_at !== undefined) {
              message.setDeliveredAt(include.attributes.deliver_at);
            }
            if (include.relationships.author !== undefined) {
              if (include.relationships.author.data.id !== undefined && users[include.relationships.author.data.id] !== undefined) {
                message.setAuthor(users[include.relationships.author.data.id]);
              }
            }
            messages[include.id] = message;
          }
        }
      }

      for (let i = 0; i < json.data.length; i++) {
        const elem = json.data[i];

        if (elem.type === undefined || elem.type !== 'messages/threads') {
          continue; // Continue if type not recognized or type isn't messages/threads
        }

        // Create new Thread
        const thread = new Thread(elem.id, elem.attributes.subject);
        if (elem.attributes.messages !== undefined) { thread.setAmountMessages(elem.attributes.messages); }

        // Getting Users if (there are users)
        if (elem.relationships !== undefined && elem.relationships.participants !== undefined) {
          for (let j = 0; j < elem.relationships.participants.data.length; j++) {
            const participant = elem.relationships.participants.data[j];

            if (participant.type === 'users' && users[participant.id] !== undefined) {
              thread.addUser(users[participant.id]);
            }
          }
        }

        // Getting Messages if there are any
        if (elem.relationships !== undefined && elem.relationships.messages !== undefined) {
          for (let j = 0; j < elem.relationships.messages.data.length; j++) {
            const msg = elem.relationships.messages.data[j];

            if (msg.type === 'messages/messages' && messages[msg.id] !== undefined) {
              thread.addMessage(messages[msg.id]);
            }
          }
        }

        // Getting amount of unread messages
        if (elem.attributes.unread !== undefined && elem.attributes.unread.is_unread === true) {
          let unreadCount = elem.attributes.unread.messages;

          if (unreadCount > thread.getMessages().length) {
            unreadCount = thread.getMessages().length;
          }

          thread.setAmountUnreadMessages(unreadCount);

          for (let x = (thread.getMessages().length - 1); x >= 0 && unreadCount > 0; x--) {
            thread.addUnreadMessage(thread.getMessages()[x]);
            unreadCount--;
          }
        }

        threads.push(thread);
      }

      retReplay.next(threads);
    });

    return retReplay;
  }

  public getThreadDetail(threadId): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.threadDetailURL + threadId).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('------------MessageService--------: Failed to get Threads of user.');
        retReplay.next(json);
        return;
      }

      // No Error occured, now get the threads
      let thread: Thread = null;
      const users = {};
      const messages = {};

      // Retrieve users if some where returned
      if (json.included !== undefined) {
        for (let i = 0; i < json.included.length; i++) {
          const include = json.included[i];

          if (include.type !== undefined && include.type === 'users') {
            const user = new User(include.id);

            if (include.attributes.name !== undefined) { user.setName(include.attributes.name); }
            if (include.attributes.email !== undefined) { user.setEmail(include.attributes.email); }
            if (include.attributes.firstname !== undefined) { user.setFirstName(include.attributes.firstname); }
            if (include.attributes.lastname !== undefined) { user.setLastName(include.attributes.lastname); }
            if (include.attributes.picture !== undefined) { user.setPicture(include.attributes.picture); }

            users[include.id] = user;
          } else if (include.type !== undefined && include.type === 'messages/messages') {
            const message = new Message(include.id);
            if (include.attributes.body !== undefined) {
              message.setMessage(include.attributes.body);
            }
            if (include.attributes.deliver_at !== undefined) {
              message.setDeliveredAt(include.attributes.deliver_at);
            }
            if (include.relationships.author !== undefined) {
              if (include.relationships.author.data.id !== undefined && users[include.relationships.author.data.id] !== undefined) {
                message.setAuthor(users[include.relationships.author.data.id]);
              }
            }
            messages[include.id] = message;
          }
        }

        // find messages if included
        for (let i = 0; i < json.included.length; i++) {
          const include = json.included[i];

          if (include.type !== undefined && include.type === 'messages/messages') {
            const message = new Message(include.id);
            if (include.attributes.body !== undefined) {
              message.setMessage(include.attributes.body);
            }
            if (include.attributes.deliver_at !== undefined) {
              message.setDeliveredAt(include.attributes.deliver_at);
            }
            if (include.relationships.author !== undefined) {
              if (include.relationships.author.data.id !== undefined && users[include.relationships.author.data.id] !== undefined) {
                message.setAuthor(users[include.relationships.author.data.id]);
              }
            }
            messages[include.id] = message;
          }
        }
      }

      thread = new Thread(json.data.id, json.data.attributes.subject);
      if (json.data.attributes.messages !== undefined) {
        thread.setAmountMessages(json.data.attributes.messages);
      }

      if (json.data.relationships !== undefined && json.data.relationships.participants !== undefined) {
        for (let j = 0; j < json.data.relationships.participants.data.length; j++) {
          const participant = json.data.relationships.participants.data[j];

          if (participant.type === 'users' && users[participant.id] !== undefined) {
            thread.addUser(users[participant.id]);
          }
        }
      }

      if (json.data.relationships !== undefined && json.data.relationships.messages !== undefined) {
        for (let j = 0; j < json.data.relationships.messages.data.length; j++) {
          const message = json.data.relationships.messages.data[j];

          if (message.type === 'messages/messages' && messages[message.id] !== undefined) {
            thread.addMessage(messages[message.id]);
          }
        }
      }

      retReplay.next(thread);
    });

    return retReplay;
  }

  /**
   * This function creates a new thread with a specific subject
   * @param thread Thread to be send
   */
  public createNewThread(thread: Thread): Subject<any> {
    const retReplay = new Subject();

    // Create data to transmit
    const postData = {
      'data': {
        'type': 'messages/threads',
        'attributes': {
          'subject': thread.getSubject()
        }
      }
    };

    this.communication.post(this.createNewThreadURL, postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('------------MessageService--------: Failed to create new Thread.');
        retReplay.next(json);
        return;
      }

      // Getting headers of new Thread

      if (json.headers !== undefined && json.headers._headers.get('location') !== undefined) {
        const splits = json.headers._headers.get('location')[0].split('/');
        const id = splits[(splits.length - 1)];
        thread.setId(id);
        retReplay.next(thread);
      } else {
        console.log('------------MessageService--------: Created thread id could not be read.');
        retReplay.next(null);
      }
    });

    return retReplay;
  }

  /**
   * This function sends a new message to a thread
   * @param thread Thread to receive new message
   * @param message message to be send
   */
  public sendMessage(thread: Thread, message: String): Subject<any> {
    const retReplay = new Subject();

    // Creating data to be send
    const postData = {
      'data': {
        'type': 'messages/messages',
        'attributes': {
          'body': message
        }
      }
    };

    this.communication.post(this.sendMessageURL.replace('##id##', thread.getId().toString()), postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('------------MessageService--------: Could not create new message in thread:' + thread.getId());
        retReplay.next(json);
        return;
      }

      // Sending message was successful
      retReplay.next(true);
    });

    return retReplay;
  }

  /**
   * This function adds participants to a specific thread
   * @param thread participants will be added to this thread
   * @param participants adding this participants to thread
   */
  public addParticipants(thread: Thread, participants: Array<User>): Subject<any> {
    const retReplay = new Subject();

    // Create data to be send
    const pIds = [];

    for (const user of participants) {
      pIds.push(user.getID());
    }

    const postData = {
      'data': {
        'type': 'users',
        'attributes': {
          'participants': pIds
        }
      }
    };

    this.communication.post(this.addParticipantsURL.replace('##id##', thread.getId().toString()), postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('------------MessageService--------: Could not add participant to thread:' + thread.getId());
        retReplay.next(json);
        return;
      }

      // Adding participants was successful
      retReplay.next(true);
    });

    return retReplay;
  }

  /**
   * This function removes a certain user from messages
   * @param thread thread the user will be removed from
   * @param user the user that will be removed
   */
  public removeParticipant(thread: Thread, user: User): Subject<any> {
    const retReplay = new Subject();

    const postData = {
      'data': {
        'type': 'users',
        'attributes': {
          'participants': [user.getID()]
        }
      }
    };

    this.communication.delete(this.addParticipantsURL.replace('##id##', thread.getId().toString()), postData).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('--------MessageService----------: Failed to remove participant');
        console.log(json);
        retReplay.next(json);
        return;
      }

      // Removing was successful
      retReplay.next(true);
    });

    return retReplay;
  }

  /**
   * This function deletes a thread
   * @param thread Thread to be deleted
   */
  public deleteThread(thread: Thread): Subject<any> {
    const retReplay = new Subject();
    const url = this.deleteThreadURL + thread.getId();

    this.communication.delete(url).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('---------MessageService-------: Error occured while deleting thread');
        retReplay.next(json);
        return;
      }

      // Deleting thread was successful
      retReplay.next(true);
    });

    return retReplay;
  }

  /**
   * This function returns the basic Update reply
   */
  public registerReadUpdate(): Subject<any> {
    if (this.updateReplay === null) {
      this.updateReplay = new Subject();
    }

    return this.updateReplay;
  }

  /**
   * Function to notify update
   */
  public notifyReadUpdate() {
    this.updateReplay.next(true);
  }

}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/message.service.ts